
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, Users, TrendingUp, Award } from "lucide-react";

export const ReferralAdminDashboard = () => {
  // Get all referral analytics
  const { data: allAnalytics, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['admin-referral-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_analytics')
        .select('*')
        .order('total_commission', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Get user profiles separately
  const { data: profiles, isLoading: isLoadingProfiles } = useQuery({
    queryKey: ['admin-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email');

      if (error) throw error;
      return data;
    },
  });

  // Get referral codes
  const { data: referralCodes, isLoading: isLoadingCodes } = useQuery({
    queryKey: ['admin-referral-codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_codes')
        .select('*');

      if (error) throw error;
      return data;
    },
  });

  // Get recent conversions
  const { data: recentConversions, isLoading: isLoadingConversions } = useQuery({
    queryKey: ['admin-recent-conversions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_conversions')
        .select('*')
        .order('converted_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
  });

  // Calculate stats
  const totalCommissionPaid = allAnalytics?.reduce((sum, record) => sum + Number(record.total_commission), 0) || 0;
  const totalClicks = allAnalytics?.reduce((sum, record) => sum + record.total_clicks, 0) || 0;
  const totalConversions = allAnalytics?.reduce((sum, record) => sum + record.total_conversions, 0) || 0;
  const averageConversionRate = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;

  // Create top performers by combining analytics with profiles
  const topPerformers = allAnalytics?.slice(0, 10).map(analytics => {
    const profile = profiles?.find(p => p.id === analytics.user_id);
    return {
      ...analytics,
      profile
    };
  }) || [];

  // Enhanced conversions with referral code and profile data
  const enhancedConversions = recentConversions?.map(conversion => {
    const referralCode = referralCodes?.find(code => code.id === conversion.referral_code_id);
    const profile = profiles?.find(p => p.id === referralCode?.user_id);
    return {
      ...conversion,
      referral_code: referralCode,
      profile
    };
  }) || [];

  if (isLoadingAnalytics || isLoadingProfiles || isLoadingCodes || isLoadingConversions) {
    return <div className="p-6">Loading admin dashboard...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Referral Admin Dashboard</h1>
          <p className="text-gray-600">Monitor referral program performance across all users</p>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Commission Paid</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">${totalCommissionPaid.toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalClicks}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalConversions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Conversion Rate</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageConversionRate.toFixed(1)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performers (All Time)</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rank</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Total Earned</TableHead>
                <TableHead>Clicks</TableHead>
                <TableHead>Conversions</TableHead>
                <TableHead>Conversion Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topPerformers.map((performer, index) => {
                const conversionRate = performer.total_clicks > 0 
                  ? (performer.total_conversions / performer.total_clicks * 100) 
                  : 0;
                
                return (
                  <TableRow key={performer.user_id}>
                    <TableCell>
                      <Badge variant={index < 3 ? "default" : "secondary"}>
                        #{index + 1}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {performer.profile?.full_name || 'Unknown'}
                    </TableCell>
                    <TableCell>{performer.profile?.email || 'Unknown'}</TableCell>
                    <TableCell className="text-green-600 font-semibold">
                      ${Number(performer.total_commission).toFixed(2)}
                    </TableCell>
                    <TableCell>{performer.total_clicks}</TableCell>
                    <TableCell>{performer.total_conversions}</TableCell>
                    <TableCell>{conversionRate.toFixed(1)}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Conversions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Conversions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Referrer</TableHead>
                <TableHead>Referral Code</TableHead>
                <TableHead>Subscription Type</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enhancedConversions.map((conversion) => (
                <TableRow key={conversion.id}>
                  <TableCell>
                    {new Date(conversion.converted_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {conversion.profile?.full_name || 'Unknown'}
                  </TableCell>
                  <TableCell className="font-mono">
                    {conversion.referral_code?.code || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{conversion.subscription_type}</Badge>
                  </TableCell>
                  <TableCell className="text-green-600">
                    ${Number(conversion.commission_amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={conversion.status === 'confirmed' ? 'default' : 'secondary'}
                    >
                      {conversion.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
